import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  useTheme,
  CircularProgress,
} from '@mui/material'
import {
  Search,
  TrendingUp,
  AutoAwesome,
  GridView,
  Image,
  Folder,
  ArrowForward,
  EmojiEvents,
  Whatshot,
  FavoriteBorder,
  RemoveRedEyeOutlined,
  ChatBubbleOutline,
} from '@mui/icons-material'
import { styled, keyframes } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useGetTopics } from '@/hooks/api/dashboard'
import { Topic } from '@/types'
import { TopicType, PrivacyType, TopicTypes } from '@/utils/constants'
import ReactTimeago from 'react-timeago'
import HeroSectionComponent from './components/HeroSection'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { BabylonPreview } from '@/components'

// Helper function to get readable topic type label
const getTopicTypeLabel = (topicType: string | number): string => {
  const typeNum = Number(topicType)
  const topicInfo = TopicTypes[typeNum]
  return topicInfo?.label || 'Other'
}

// ==================== ANIMATIONS ====================
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
`

// ==================== STYLED COMPONENTS ====================
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '800px',
    height: '800px',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${float} 20s ease-in-out infinite`,
    pointerEvents: 'none',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '-5%',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${float} 25s ease-in-out infinite`,
    animationDelay: '5s',
    pointerEvents: 'none',
  },
}))

const ContentWrapper = styled(Box)({
  position: 'relative',
  zIndex: 1,
  maxWidth: '1600px',
  margin: '0 auto',
  padding: '40px 32px',
})

const CategoryChip = styled(Chip)(({ theme }) => ({
  height: '38px',
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  padding: '0 6px',
  backgroundColor: '#ffffff',
  border: '2px solid #e2e8f0',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)',
  cursor: 'pointer',

  '& .MuiChip-icon': {
    marginLeft: '6px',
    color: '#64748b',
    fontSize: '18px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(99, 102, 241, 0.4)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(99, 102, 241, 0.15)',

    '& .MuiChip-icon': {
      color: '#6366f1',
      transform: 'scale(1.1) rotate(-5deg)',
    },
  },

  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },

  '&.MuiChip-filled': {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    border: '2px solid transparent',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',

    '& .MuiChip-icon': {
      color: '#ffffff',
    },

    '&:hover': {
      background: 'linear-gradient(135deg, #5558e3 0%, #7c48e8 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(99, 102, 241, 0.45)',
    },

    '&:active': {
      transform: 'translateY(-1px) scale(0.98)',
    },
  },
}))

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.02em',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',

  '& .icon': {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    fontSize: '18px',
  },
})

const FeaturedCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '20px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  border: '2px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '20px',
    padding: '2px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0) 0%, rgba(168, 85, 247, 0) 100%)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'transparent',
    boxShadow: `
      0 20px 60px rgba(99, 102, 241, 0.2),
      0 0 0 1px rgba(99, 102, 241, 0.1)
    `,

    '&::before': {
      opacity: 1,
      background:
        'linear-gradient(135deg, rgba(99, 102, 241, 0.6) 0%, rgba(168, 85, 247, 0.6) 100%)',
    },

    '& .featured-image': {
      transform: 'scale(1.05)',
    },

    '& .featured-overlay': {
      opacity: 1,
    },

    '& .featured-action': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

const BentoCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '18px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  border: '2px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',

  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
    boxShadow: '0 12px 32px rgba(99, 102, 241, 0.15)',

    '& .bento-image': {
      transform: 'scale(1.08)',
    },

    '& .bento-info': {
      background: 'linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.85) 100%)',
    },
  },
}))

const StatBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  borderRadius: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  fontSize: '13px',
  fontWeight: 600,
  color: '#ffffff',
  pointerEvents: 'none',

  '& svg': {
    fontSize: '16px',
  },
})

const CreatorAvatar = styled(Avatar)({
  width: 32,
  height: 32,
  border: '2px solid #ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    transform: 'scale(1.15)',
    zIndex: 2,
  },
})

const TrendingBadge = styled(Box)({
  position: 'absolute',
  top: '12px',
  right: '12px',
  padding: '6px 12px',
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  color: '#ffffff',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
  zIndex: 2,
  pointerEvents: 'none',

  '& svg': {
    fontSize: '16px',
  },
})

const GradientButton = styled(Button)({
  height: '48px',
  borderRadius: '12px',
  fontSize: '15px',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  textTransform: 'none',
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#ffffff',
  border: '2px solid transparent',
  boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: '0 24px',

  '&:hover': {
    background: 'linear-gradient(135deg, #5558e3 0%, #7c48e8 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(99, 102, 241, 0.4)',
  },

  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },
})

// ==================== INTERFACES ====================
interface DiscoverHomeProps {
  topicType?: TopicType
  privacyType?: PrivacyType
}

// ==================== MAIN COMPONENT ====================
export const DiscoverHome: React.FC<DiscoverHomeProps> = ({
  topicType = TopicType.ALL,
  privacyType = PrivacyType.PUBLIC,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [topics, setTopics] = useState<Topic[]>([])

  const getTopics = useGetTopics()

  useEffect(() => {
    getTopics.mutate(
      {
        pageNo: 1,
        interaction: privacyType === PrivacyType.PUBLIC ? 1 : 0,
        topicType: topicType,
      },
      {
        onSuccess: (data: Topic[]) => {
          setTopics(data || [])
        },
      },
    )
  }, [topicType, privacyType])

  // Filter topics by category
  const filteredTopics = useMemo(() => {
    let filtered = topics

    console.log('🔍 Filtering topics:', {
      totalTopics: topics.length,
      selectedCategory,
      sampleTopicTypes: topics
        .slice(0, 3)
        .map((t) => ({ id: t.topicID, type: t.topicType, typeOf: typeof t.topicType })),
    })

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((topic: Topic) => {
        // Convert both to strings for comparison since topicType can be string or number
        const topicTypeStr = String(topic.topicType)

        if (selectedCategory === 'pattern') return topicTypeStr === String(TopicType.PATTERN)
        if (selectedCategory === 'album') return topicTypeStr === String(TopicType.ALBUM)
        if (selectedCategory === 'gallery') return topicTypeStr === String(TopicType.GALLERY)
        return true
      })

      console.log('✅ After category filter:', {
        selectedCategory,
        filteredCount: filtered.length,
        sampleFiltered: filtered
          .slice(0, 2)
          .map((t) => ({ id: t.topicID, name: t.topicName, type: t.topicType })),
      })
    }

    if (searchQuery) {
      filtered = filtered.filter((topic: Topic) =>
        topic.topicName.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    }

    return filtered
  }, [topics, selectedCategory, searchQuery])

  // Filter chips should only show types that exist in the current data
  const categories = useMemo(() => {
    const cats = [{ id: 'all', label: 'All', icon: <GridView /> }]

    // Only show filters for types that exist in the data
    const hasPatterns = topics.some((t) => String(t.topicType) === String(TopicType.PATTERN))
    const hasAlbums = topics.some((t) => String(t.topicType) === String(TopicType.ALBUM))
    const hasGalleries = topics.some((t) => String(t.topicType) === String(TopicType.GALLERY))

    if (hasPatterns) cats.push({ id: 'pattern', label: 'Patterns', icon: <AutoAwesome /> })
    if (hasAlbums) cats.push({ id: 'album', label: 'Albums', icon: <Folder /> })
    if (hasGalleries) cats.push({ id: 'gallery', label: 'Galleries', icon: <Image /> })

    return cats
  }, [topics])

  const featuredTopics = useMemo(() => filteredTopics.slice(0, 3), [filteredTopics])
  const trendingTopics = useMemo(() => filteredTopics.slice(3, 9), [filteredTopics])
  const recentTopics = useMemo(() => filteredTopics.slice(9, 21), [filteredTopics])

  const handleTopicClick = (topic: Topic) => {
    // ORIGINAL behavior: ALL topics navigate to /preview with topicID and topicType
    // The preview page handles whether to show edit or view mode based on ownership
    navigate(`/preview?topicID=${topic.topicID}&topicType=${topic.topicType}`)
  }

  // Page titles based on topic type
  const pageTitle = useMemo(() => {
    if (topicType === TopicType.PATTERN) return 'Discover Patterns'
    if (topicType === TopicType.ALBUM) return 'Discover Albums'
    if (topicType === TopicType.GALLERY) return 'Discover Galleries'
    return 'Discover Amazing Creations'
  }, [topicType])

  const pageDescription = useMemo(() => {
    if (topicType === TopicType.PATTERN)
      return 'Explore thousands of beautiful patterns and designs created by talented designers from around the world.'
    if (topicType === TopicType.ALBUM)
      return 'Browse stunning photo albums and collections shared by our creative community.'
    if (topicType === TopicType.GALLERY)
      return 'Discover inspiring galleries showcasing the best creative work.'
    return 'Explore thousands of beautiful patterns, albums, and galleries created by talented designers from around the world.'
  }, [topicType])

  // Theme colors based on topic type
  const themeColors = useMemo(() => {
    if (topicType === TopicType.PATTERN) {
      return {
        primary: '#8b5cf6', // purple
        secondary: '#a78bfa',
        light: 'rgba(139, 92, 246, 0.12)',
        icon: <AutoAwesome />,
        gradients: [
          'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(167, 139, 250, 0.12) 100%)',
          'linear-gradient(135deg, rgba(167, 139, 250, 0.15) 0%, rgba(196, 181, 253, 0.15) 100%)',
          'linear-gradient(135deg, rgba(196, 181, 253, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
          'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 50%, #c4b5fd 100%)',
        ],
      }
    }
    if (topicType === TopicType.ALBUM) {
      return {
        primary: '#f97316', // vibrant orange
        secondary: '#fb923c',
        light: 'rgba(249, 115, 22, 0.12)',
        icon: <SVG src={SVGS.TabAlbum} width={16} height={16} />,
        gradients: [
          'linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(251, 146, 60, 0.12) 100%)',
          'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(253, 186, 116, 0.15) 100%)',
          'linear-gradient(135deg, rgba(253, 186, 116, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%)',
          'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
        ],
      }
    }
    if (topicType === TopicType.GALLERY) {
      return {
        primary: '#14b8a6', // teal
        secondary: '#2dd4bf',
        light: 'rgba(20, 184, 166, 0.12)',
        icon: <Image />,
        gradients: [
          'linear-gradient(135deg, rgba(20, 184, 166, 0.12) 0%, rgba(45, 212, 191, 0.12) 100%)',
          'linear-gradient(135deg, rgba(45, 212, 191, 0.15) 0%, rgba(94, 234, 212, 0.15) 100%)',
          'linear-gradient(135deg, rgba(94, 234, 212, 0.15) 0%, rgba(20, 184, 166, 0.15) 100%)',
          'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 50%, #5eead4 100%)',
        ],
      }
    }
    // Default (All)
    return {
      primary: '#6366f1', // indigo
      secondary: '#8b5cf6',
      light: 'rgba(99, 102, 241, 0.12)',
      icon: <AutoAwesome />,
      gradients: [
        'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%)',
        'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
        'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
        'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
      ],
    }
  }, [topicType])

  return (
    <PageContainer>
      <ContentWrapper>
        {/* HERO SECTION */}
        <HeroSectionComponent
          topicType={topicType}
          pageTitle={pageTitle}
          pageDescription={pageDescription}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          themeColors={themeColors}
        >
          {/* Category Filters */}
          {topicType === TopicType.ALL && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {categories.map((category) => (
                <CategoryChip
                  key={category.id}
                  label={category.label}
                  icon={category.icon}
                  onClick={() => setSelectedCategory(category.id)}
                  variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          )}
        </HeroSectionComponent>

        {getTopics.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={48} thickness={3} sx={{ color: '#6366f1' }} />
          </Box>
        ) : (
          <>
            {/* FEATURED SECTION */}
            {featuredTopics.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <SectionTitle>
                  <Box className='icon'>
                    <EmojiEvents />
                  </Box>
                  Featured Creations
                </SectionTitle>

                <Grid container spacing={3}>
                  {featuredTopics.map((topic: Topic, index: number) => (
                    <Grid item xs={12} md={index === 0 ? 12 : 6} key={topic.topicID}>
                      <FeaturedCard
                        onClick={() => handleTopicClick(topic)}
                        sx={{
                          height: index === 0 ? '450px' : '320px',
                          animation: `${slideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${
                            index * 0.1
                          }s backwards`,
                        }}
                      >
                        {/* Background - Babylon Preview for first card, Image for others */}
                        {index === 0 ? (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              '& canvas': {
                                transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              },
                              '&:hover canvas': {
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <BabylonPreview topicID={topic.topicID} height='100%' />
                          </Box>
                        ) : (
                          <Box
                            className='featured-image'
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              backgroundImage:
                                topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                                  ? `url(${
                                      topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                                    })`
                                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        )}

                        {/* Gradient Overlay */}
                        <Box
                          className='featured-overlay'
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                              'linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.7) 70%, rgba(15, 23, 42, 0.9) 100%)',
                            opacity: 0.8,
                            transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            pointerEvents: 'none',
                          }}
                        />

                        {/* Trending Badge */}
                        {index === 0 && (
                          <TrendingBadge>
                            <Whatshot />
                            Trending
                          </TrendingBadge>
                        )}

                        {/* Stats */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '16px',
                            left: '16px',
                            display: 'flex',
                            gap: 1.5,
                            zIndex: 2,
                            flexWrap: 'wrap',
                            pointerEvents: 'none',
                          }}
                        >
                          <StatBadge>
                            <FavoriteBorder />
                            {Number(topic.totalLikes) || 0}
                          </StatBadge>
                          <StatBadge>
                            <RemoveRedEyeOutlined />
                            {Number(topic.visitorVisits || 0) + Number(topic.ownerVisits || 0)}
                          </StatBadge>
                          {Number(topic.totalComments) > 0 && (
                            <StatBadge>
                              <ChatBubbleOutline />
                              {Number(topic.totalComments) || 0}
                            </StatBadge>
                          )}
                        </Box>

                        {/* Content */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: { xs: '24px', md: '32px' },
                            zIndex: 2,
                          }}
                        >
                          <Chip
                            label={getTopicTypeLabel(topic.topicType)}
                            size='small'
                            sx={{
                              mb: 2,
                              backgroundColor: 'rgba(99, 102, 241, 0.2)',
                              backdropFilter: 'blur(8px)',
                              color: '#ffffff',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          />

                          <Typography
                            sx={{
                              fontSize: { xs: '24px', md: index === 0 ? '36px' : '28px' },
                              fontWeight: 700,
                              color: '#ffffff',
                              letterSpacing: '-0.02em',
                              mb: 2,
                              lineHeight: 1.2,
                            }}
                          >
                            {topic.topicName}
                          </Typography>

                          {topic.topicDescription && (
                            <Typography
                              sx={{
                                fontSize: '15px',
                                fontWeight: 500,
                                color: 'rgba(255, 255, 255, 0.9)',
                                mb: 3,
                                lineHeight: 1.6,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {topic.topicDescription}
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <CreatorAvatar src={topic.profilePicThumbURL} alt={topic.userName}>
                                {topic.userName?.[0]}
                              </CreatorAvatar>
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#ffffff',
                                  }}
                                >
                                  {topic.userName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: 'rgba(255, 255, 255, 0.7)',
                                  }}
                                >
                                  <ReactTimeago date={topic.dateCreated} />
                                </Typography>
                              </Box>
                            </Box>

                            <Box
                              className='featured-action'
                              sx={{
                                opacity: 0,
                                transform: 'translateY(10px)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              }}
                            >
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleTopicClick(topic)
                                }}
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  backdropFilter: 'blur(8px)',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  color: '#ffffff',
                                  '&:hover': {
                                    backgroundColor: '#6366f1',
                                    transform: 'scale(1.1)',
                                  },
                                }}
                              >
                                <ArrowForward />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      </FeaturedCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* TRENDING / MAIN GRID SECTION */}
            {trendingTopics.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <SectionTitle sx={{ mb: 0 }}>
                    <Box className='icon'>
                      <TrendingUp />
                    </Box>
                    {topicType === TopicType.ALL
                      ? 'Trending Now'
                      : `Popular ${
                          topicType === TopicType.PATTERN
                            ? 'Patterns'
                            : topicType === TopicType.ALBUM
                            ? 'Albums'
                            : 'Galleries'
                        }`}
                  </SectionTitle>

                  <GradientButton
                    endIcon={<ArrowForward />}
                    onClick={() => {
                      // Scroll to recently added section or show all
                      window.scrollTo({
                        top: document.documentElement.scrollHeight,
                        behavior: 'smooth',
                      })
                    }}
                  >
                    View All
                  </GradientButton>
                </Box>

                <Grid container spacing={2.5}>
                  {trendingTopics.map((topic: Topic, index: number) => (
                    <Grid item xs={12} sm={6} md={4} key={topic.topicID}>
                      <BentoCard
                        onClick={() => handleTopicClick(topic)}
                        sx={{
                          animation: `${scaleIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${
                            index * 0.05
                          }s backwards`,
                        }}
                      >
                        {/* Image */}
                        <Box
                          className='bento-image'
                          sx={{
                            height: '220px',
                            backgroundImage:
                              topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                                ? `url(${
                                    topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                                  })`
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                          }}
                        >
                          {/* Gradient Overlay */}
                          <Box
                            className='bento-info'
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              padding: '16px',
                              background:
                                'linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.5) 100%)',
                              transition: 'background 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </Box>

                        {/* Content */}
                        <Box sx={{ padding: '16px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Chip
                              label={getTopicTypeLabel(topic.topicType)}
                              size='small'
                              sx={{
                                height: '24px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                color: '#6366f1',
                                border: '1px solid rgba(99, 102, 241, 0.2)',
                              }}
                            />
                          </Box>

                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 700,
                              color: '#0f172a',
                              letterSpacing: '-0.01em',
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {topic.topicName}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              pt: 1.5,
                              borderTop: '1px solid rgba(226, 232, 240, 0.6)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CreatorAvatar
                                src={topic.profilePicThumbURL}
                                alt={topic.userName}
                                sx={{ width: 28, height: 28 }}
                              >
                                {topic.userName?.[0]}
                              </CreatorAvatar>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#64748b',
                                }}
                              >
                                {topic.userName}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: '#94a3b8',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                }}
                              >
                                <FavoriteBorder sx={{ fontSize: 16 }} />
                                {Number(topic.totalLikes) || 0}
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: '#94a3b8',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                }}
                              >
                                <RemoveRedEyeOutlined sx={{ fontSize: 16 }} />
                                {Number(topic.visitorVisits || 0) + Number(topic.ownerVisits || 0)}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </BentoCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* RECENT SECTION */}
            {recentTopics.length > 0 && (
              <Box>
                <SectionTitle>
                  <Box className='icon'>
                    <AutoAwesome />
                  </Box>
                  Recently Added
                </SectionTitle>

                <Grid container spacing={2.5}>
                  {recentTopics.map((topic: Topic, index: number) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={topic.topicID}>
                      <BentoCard
                        onClick={() => handleTopicClick(topic)}
                        sx={{
                          animation: `${scaleIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${
                            index * 0.03
                          }s backwards`,
                        }}
                      >
                        <Box
                          className='bento-image'
                          sx={{
                            height: '180px',
                            backgroundImage:
                              topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                                ? `url(${
                                    topic.latestEgo?.photoAlbumURL || topic.profilePicThumbURL
                                  })`
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />

                        <Box sx={{ padding: '14px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip
                              label={getTopicTypeLabel(topic.topicType)}
                              size='small'
                              sx={{
                                height: '22px',
                                fontSize: '10px',
                                fontWeight: 600,
                                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                color: '#6366f1',
                                border: '1px solid rgba(99, 102, 241, 0.2)',
                              }}
                            />
                          </Box>

                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 700,
                              color: '#0f172a',
                              letterSpacing: '-0.01em',
                              mb: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {topic.topicName}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              pt: 1.5,
                              borderTop: '1px solid rgba(226, 232, 240, 0.6)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <CreatorAvatar
                                src={topic.profilePicThumbURL}
                                alt={topic.userName}
                                sx={{ width: 24, height: 24 }}
                              >
                                {topic.userName?.[0]}
                              </CreatorAvatar>
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#64748b',
                                }}
                              >
                                {topic.userName}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: '#94a3b8',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                <FavoriteBorder sx={{ fontSize: 14 }} />
                                {Number(topic.totalLikes) || 0}
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: '#94a3b8',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                <RemoveRedEyeOutlined sx={{ fontSize: 14 }} />
                                {Number(topic.visitorVisits || 0) + Number(topic.ownerVisits || 0)}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </BentoCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {filteredTopics.length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 10,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#64748b',
                  }}
                >
                  No creations found
                </Typography>
              </Box>
            )}
          </>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default DiscoverHome
